import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Check } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const userDetailsSchema = z.object({
  email: z.string().email("Geçerli bir email adresi giriniz"),
  password: z.string().min(6, "Şifre en az 6 karakter olmalıdır"),
  confirmPassword: z.string().min(6, "Şifre en az 6 karakter olmalıdır"),
  nameSurname: z.string().min(2, "İsim ve soyisim en az 2 karakter olmalıdır"),
  phone: z.string().min(10, "Geçerli bir telefon numarası giriniz"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Şifreler eşleşmiyor",
  path: ["confirmPassword"],
});

export const UserDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<any>(null);
  const returnTo = location.state?.returnTo;
  const coach = location.state?.coach;

  useEffect(() => {
    if (!coach && !returnTo) {
      const storedCoach = JSON.parse(localStorage.getItem('selectedCoach') || '{}');
      if (!storedCoach.id) {
        navigate('/coaches');
        return;
      }
      setSelectedCoach(storedCoach);
    } else if (coach) {
      setSelectedCoach(coach);
    }
  }, [coach, navigate, returnTo]);

  const form = useForm({
    resolver: zodResolver(userDetailsSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      nameSurname: "",
      phone: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof userDetailsSchema>) => {
    try {
      setIsLoading(true);

      const { error: signUpError, data: authData } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            name_surname: data.nameSurname,
            phone: data.phone,
          },
        },
      });

      if (signUpError) throw signUpError;

      localStorage.setItem('userDetails', JSON.stringify({
        email: data.email,
        nameSurname: data.nameSurname,
        phone: data.phone,
      }));

      toast({
        title: "Başarılı",
        description: returnTo 
          ? "Hesabınız oluşturuldu. Yönlendiriliyorsunuz." 
          : "Hesabınız oluşturuldu. Ödeme sayfasına yönlendiriliyorsunuz.",
      });

      // If returnTo is specified, navigate there with the coach data
      if (returnTo) {
        navigate(returnTo, { state: { coach } });
      } else {
        navigate('/payment');
      }
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: error.message || "Kayıt olurken bir hata oluştu. Lütfen tekrar deneyiniz.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-secondary to-brand-primary p-4 pt-28">
      <div className="container max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
          {/* Left side - Logo and Features */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-white">
                Mentorunuzla birlikte başarıya ulaşın
              </h1>
              <p className="text-xl text-white/80">
                Binlerce öğrenci gibi siz de hedeflerinize ulaşın
              </p>
            </div>
            
            <div className="space-y-4">
              {[
                "Kişiselleştirilmiş mentörlük desteği",
                "Düzenli görüşme ve takip sistemi",
                "Başarı garantili program",
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-white">
                  <Check className="h-5 w-5 text-brand-accent" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Sign up form */}
          <Card className="w-full">
            <CardContent className="p-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="ornek@email.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Şifre</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="********" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Şifre Tekrar</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="********" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="nameSurname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ad Soyad</FormLabel>
                        <FormControl>
                          <Input placeholder="Ad Soyad" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Telefon</FormLabel>
                        <FormControl>
                          <Input placeholder="05XX XXX XX XX" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Kaydediliyor..." : "Devam Et"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>

        {/* Selected Coach Card */}
        {selectedCoach && (
          <Card className="w-full bg-white/5 backdrop-blur-sm border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-6">
                <div className="relative w-24 h-24 overflow-hidden rounded-full">
                  <img 
                    src={`/coaches/${selectedCoach.photo_url}.jpg` || '/placeholder.svg'} 
                    alt={selectedCoach.name_surname}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {selectedCoach.name_surname}
                  </h3>
                  <p className="text-white/80">
                    {selectedCoach.university} - {selectedCoach.major}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-white">
                    {selectedCoach.monthly_price}₺
                    <span className="text-sm font-normal text-white/60">/ay</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UserDetails;